import { Button, ButtonGroup, Divider, Icon } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import styled from 'styled-components';
import { ISurveyBuilderActionCreators, SurveyStep } from '../../models';
import { ISurveyBuilderState } from '../../store/SurveyBuilderStore';
import { SurveyBuilderHelper } from '../../utilities';
import { ClearDiv } from '../common';
import { SurveyStepItem } from './SurveyStepItem';
import SurveySubStepItem from './SurveySubStepItem';

export type SurveyStepMenuProps = Pick<ISurveyBuilderActionCreators, 'Jump' | 'NextStep' | 'PreviousStep'> &
	Pick<ISurveyBuilderState, 'surveyId' | 'currentStep' | 'notes'>;

export const MenuContainer = styled.div.attrs<{ disabled?: boolean }>({
	className: 'tws-stepper-navigation',
})<{ disabled?: boolean }>`
	.btn-group > .btn.btn-xs {
		padding: 5px;
		width: 22px;
		${(props) => props.disabled && 'pointer-events: none;'}
	}
`;

export class SurveyStepMenu extends React.Component<SurveyStepMenuProps> {
	constructor(props: SurveyStepMenuProps) {
		super(props);

		this.jumpTo = this.jumpTo.bind(this);
		this.next = this.next.bind(this);
		this.previous = this.previous.bind(this);
	}

	public render() {
		const { currentStep } = this.props;
		const restrictNavigation = this.props.surveyId === 0 || this.props.currentStep === SurveyStep.Complete;

		return (
			<MenuContainer>
				<Divider type='dashed' />
				<ClearDiv clearHeight={'10px'} />
				<SurveyStepItem
					id='StepItemBasics'
					step={SurveyStep.Basics}
					onClick={() => this.jumpTo(SurveyStep.Basics)}
					disabled={restrictNavigation}
					isActive={currentStep === SurveyStep.Basics}
					hasNote={SurveyBuilderHelper.hasNote(this.props.notes, SurveyStep.Basics)}
				/>
				<SurveyStepItem
					id='StepItemDefine'
					step={SurveyStep.Define}
					onClick={() => this.jumpTo(SurveyStep.Define)}
					disabled={restrictNavigation}
					isActive={
						currentStep == SurveyStep.Define || currentStep == SurveyStep.DefineQuestions || currentStep == SurveyStep.ManageDimensions
					}
					hasNote={false}
				>
					<SurveySubStepItem
						id='SubStepItemDefineBasicContent'
						step={SurveyStep.Define}
						onClick={() => this.jumpTo(SurveyStep.Define)}
						disabled={restrictNavigation}
						isActive={currentStep === SurveyStep.Define}
						hasNote={SurveyBuilderHelper.hasNote(this.props.notes, SurveyStep.Define)}
					/>
					<SurveySubStepItem
						id='SubStepItemDefineQuestions'
						step={SurveyStep.DefineQuestions}
						onClick={() => this.jumpTo(SurveyStep.DefineQuestions)}
						disabled={restrictNavigation}
						isActive={currentStep === SurveyStep.DefineQuestions}
						hasNote={SurveyBuilderHelper.hasNote(this.props.notes, SurveyStep.DefineQuestions)}
					/>
					<SurveySubStepItem
						id='SubStepItemManageDimensions'
						step={SurveyStep.ManageDimensions}
						onClick={() => this.jumpTo(SurveyStep.ManageDimensions)}
						disabled={restrictNavigation}
						isActive={currentStep === SurveyStep.ManageDimensions}
						hasNote={SurveyBuilderHelper.hasNote(this.props.notes, SurveyStep.ManageDimensions)}
					/>
				</SurveyStepItem>
				<SurveyStepItem
					id='StepItemDesign'
					step={SurveyStep.BrandingAndTheme}
					onClick={() => this.jumpTo(SurveyStep.BrandingAndTheme)}
					disabled={restrictNavigation}
					isActive={currentStep === SurveyStep.BrandingAndTheme || currentStep === SurveyStep.LogicAndLayout}
					hasNote={false}
				>
					<SurveySubStepItem
						id='SubStepItemBrandingAndTheme'
						step={SurveyStep.BrandingAndTheme}
						onClick={() => this.jumpTo(SurveyStep.BrandingAndTheme)}
						disabled={restrictNavigation}
						isActive={currentStep === SurveyStep.BrandingAndTheme}
						hasNote={SurveyBuilderHelper.hasNote(this.props.notes, SurveyStep.BrandingAndTheme)}
					/>
					<SurveySubStepItem
						id='SubStepItemLogicAndLayout'
						step={SurveyStep.LogicAndLayout}
						onClick={() => this.jumpTo(SurveyStep.LogicAndLayout)}
						disabled={restrictNavigation}
						isActive={currentStep === SurveyStep.LogicAndLayout}
						hasNote={SurveyBuilderHelper.hasNote(this.props.notes, SurveyStep.LogicAndLayout)}
					/>
				</SurveyStepItem>
				<SurveyStepItem
					id='StepItemDistribution'
					step={SurveyStep.Distribution}
					onClick={() => this.jumpTo(SurveyStep.Distribution)}
					disabled={restrictNavigation}
					isActive={currentStep === SurveyStep.Distribution}
					hasNote={SurveyBuilderHelper.hasNote(this.props.notes, SurveyStep.Distribution)}
				/>
				<SurveyStepItem
					id='StepItemReporting'
					step={SurveyStep.Reporting}
					onClick={() => this.jumpTo(SurveyStep.Reporting)}
					disabled={restrictNavigation}
					isActive={currentStep === SurveyStep.Reporting}
					hasNote={SurveyBuilderHelper.hasNote(this.props.notes, SurveyStep.Reporting)}
				/>
				<SurveyStepItem
					id='StepItemConfirm'
					step={SurveyStep.Confirm}
					onClick={() => this.jumpTo(SurveyStep.Confirm)}
					disabled={restrictNavigation}
					isActive={currentStep === SurveyStep.Confirm}
					hasNote={SurveyBuilderHelper.hasNote(this.props.notes, SurveyStep.Confirm)}
				/>
				<ClearDiv clearHeight={'10px'} />
				<Divider type='dashed' />
				<ButtonGroup>
					<Button
						id='previous'
						aria-label='Previous'
						size='xs'
						onClick={this.previous}
						disabled={currentStep === SurveyStep.Basics || restrictNavigation}
					>
						<Icon name='angle-left' />
					</Button>
					<Button
						id='next'
						aria-label='Next'
						size='xs'
						onClick={this.next}
						disabled={currentStep === SurveyStep.Confirm || restrictNavigation}
					>
						<Icon name='angle-right' />
					</Button>
				</ButtonGroup>
			</MenuContainer>
		);
	}

	public jumpTo(surveyStep: SurveyStep) {
		const { surveyId } = this.props;
		if (surveyId !== undefined && surveyId !== 0) {
			this.props.Jump(surveyStep);
		}
	}

	public previous() {
		const { currentStep, surveyId } = this.props;
		if (surveyId !== undefined && currentStep !== SurveyStep.Basics && surveyId !== 0) {
			this.props.PreviousStep();
		}
	}

	public next() {
		const { currentStep, surveyId } = this.props;
		if (surveyId !== undefined && currentStep !== SurveyStep.Confirm && surveyId !== 0) {
			this.props.NextStep();
		}
	}
}
